import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useFranchiseStore } from '../../../../store/useFranchise';
import { useLoginStore } from '../../../../store/useLogin';
import { IConsultOrderCodeSearch } from '../../../../store/useConsultOrderCode';

interface IConsultManagementFilter {
  onSearch: (search: IConsultOrderCodeSearch) => void;
  onReset: () => void;
}

/**
 * @author claude
 * @date 2026.05.06
 * @description 상담 랜딩 트래킹 코드 관리 페이지 검색 필터
 */
const ConsultManagementFilter = ({ onSearch, onReset }: IConsultManagementFilter) => {
  const { role } = useLoginStore();
  const { allFranchises, getAllFranchises } = useFranchiseStore();

  const [campaignName, setCampaignName] = useState('');
  const [trkId, setTrkId] = useState('');
  const [selectedFranchiseIds, setSelectedFranchiseIds] = useState<string[]>([]);
  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState(false);
  const [franchiseSearchText, setFranchiseSearchText] = useState('');

  useEffect(() => {
    if (role === 'ROLE_ADMIN') {
      getAllFranchises();
    }
  }, [role, getAllFranchises]);

  // 외부 클릭 시 가맹점 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.consult-filter-franchise-dropdown')) {
        setShowFranchiseDropdown(false);
        setFranchiseSearchText('');
      }
    };

    if (showFranchiseDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFranchiseDropdown]);

  const handleToggleFranchise = useCallback((franchiseId: string) => {
    setSelectedFranchiseIds(prev =>
      prev.includes(franchiseId)
        ? prev.filter(id => id !== franchiseId)
        : [...prev, franchiseId]
    );
  }, []);

  const filteredFranchises = useMemo(
    () =>
      allFranchises.filter(franchise =>
        franchise.companyName?.toLowerCase().includes(franchiseSearchText.toLowerCase())
      ),
    [allFranchises, franchiseSearchText]
  );

  const selectedFranchiseLabel = useMemo(() => {
    if (selectedFranchiseIds.length === 0) {
      return '전체 가맹점';
    }
    if (selectedFranchiseIds.length === 1) {
      return (
        allFranchises.find(franchise => franchise.id === selectedFranchiseIds[0])?.companyName ||
        '가맹점'
      );
    }
    return `${selectedFranchiseIds.length}개 선택`;
  }, [allFranchises, selectedFranchiseIds]);

  const handleSearchClick = useCallback(() => {
    onSearch({
      campaignName: campaignName.trim() || undefined,
      trkId: trkId.trim() || undefined,
      franchiseIds: selectedFranchiseIds.length ? selectedFranchiseIds : undefined,
    });
  }, [campaignName, trkId, selectedFranchiseIds, onSearch]);

  const handleResetClick = useCallback(() => {
    setCampaignName('');
    setTrkId('');
    setSelectedFranchiseIds([]);
    setFranchiseSearchText('');
    setShowFranchiseDropdown(false);
    onReset();
  }, [onReset]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSearchClick();
      }
    },
    [handleSearchClick]
  );

  return (
    <div className="!flex !flex-wrap !items-end !gap-3 !p-3 !bg-gray-50 !rounded">
      <div>
        <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">캠페인명</label>
        <input
          type="text"
          value={campaignName}
          onChange={e => setCampaignName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="캠페인명으로 검색"
          className="!h-10 !px-3 !border !border-gray-300 !rounded-md !text-sm"
          style={{ width: 200 }}
        />
      </div>
      <div>
        <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">트래킹 ID</label>
        <input
          type="text"
          value={trkId}
          onChange={e => setTrkId(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="트래킹 ID 검색"
          className="!h-10 !px-3 !border !border-gray-300 !rounded-md !text-sm"
          style={{ width: 160 }}
        />
      </div>
      {role === 'ROLE_ADMIN' && (
        <div>
          <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">가맹점</label>
          <div className="consult-filter-franchise-dropdown !relative" style={{ width: 220 }}>
            <button
              type="button"
              className="!w-full !h-10 !px-3 !border !border-gray-300 !rounded-md !text-sm !bg-white !text-left !flex !items-center !justify-between"
              onClick={() => setShowFranchiseDropdown(prev => !prev)}
            >
              <span className="!truncate">{selectedFranchiseLabel}</span>
              <span className="!text-xs !text-gray-500">▼</span>
            </button>
            {showFranchiseDropdown && (
              <div className="!absolute !left-0 !right-0 !mt-1 !bg-white !border !border-gray-200 !rounded-lg !shadow-lg !z-20">
                <div className="!p-2 !border-b !border-gray-200">
                  <input
                    type="text"
                    className="!w-full !px-3 !py-2 !border !border-gray-300 !rounded-md !text-sm"
                    placeholder="가맹점 검색"
                    value={franchiseSearchText}
                    onChange={e => setFranchiseSearchText(e.target.value)}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                <div className="!max-h-56 !overflow-y-auto">
                  {filteredFranchises.map(franchise => (
                    <label
                      key={franchise.id}
                      className="!flex !items-center !gap-2 !px-3 !py-2 hover:!bg-gray-50 !text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFranchiseIds.includes(franchise.id)}
                        onChange={() => handleToggleFranchise(franchise.id)}
                      />
                      <span className="!truncate">{franchise.companyName}</span>
                    </label>
                  ))}
                  {filteredFranchises.length === 0 && (
                    <div className="!px-3 !py-3 !text-sm !text-gray-500">
                      검색 결과가 없습니다.
                    </div>
                  )}
                </div>
                <div className="!flex !justify-between !px-3 !py-2 !border-t !border-gray-200">
                  <button
                    type="button"
                    className="!text-xs !text-red-600"
                    onClick={() => setSelectedFranchiseIds([])}
                  >
                    전체 해제
                  </button>
                  <button
                    type="button"
                    className="!text-xs !text-blue-600"
                    onClick={() => setShowFranchiseDropdown(false)}
                  >
                    닫기
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <button
        className="!h-10 !px-4 !bg-blue-600 !text-white !rounded-md hover:!bg-blue-700 !flex !items-center !gap-2"
        onClick={handleSearchClick}
      >
        <FontAwesomeIcon icon={faSearch} />
        검색
      </button>
      <button
        className="!h-10 !px-4 !bg-gray-200 !text-gray-700 !rounded-md hover:!bg-gray-300"
        onClick={handleResetClick}
      >
        초기화
      </button>
    </div>
  );
};

export default ConsultManagementFilter;
